// ---------------------------------------------------------------------------
// Semiconductor Supply Chain Intelligence
// Tracks fab locations, critical dependencies, geopolitical risk exposure
// ---------------------------------------------------------------------------

export type ChipNode = '3nm' | '4nm' | '5nm' | '7nm' | '10nm' | '14nm' | '28nm' | 'mature';

export interface SemiFab {
  id: string;
  company: string;
  country: string;
  countryCode: string;
  city: string;
  lat: number;
  lon: number;
  node: ChipNode;          // most advanced process node
  capacity: string;        // monthly wafer capacity
  customers: string[];     // major customers
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  riskReason: string;
  globalSharePct: number;  // % of global advanced chip production
}

export interface SemiEquipment {
  company: string;
  country: string;
  countryCode: string;
  product: string;
  monopolyPct: number;     // market share %
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  note: string;
}

export interface SemiMaterial {
  material: string;
  topSupplier: string;
  supplierCountry: string;
  countryCode: string;
  globalSharePct: number;
  riskLevel: 'critical' | 'high' | 'medium' | 'low';
  note: string;
}

export interface SemiChoke {
  id: string;
  title: string;
  type: 'fab' | 'equipment' | 'material' | 'design';
  severity: 'critical' | 'high' | 'medium';
  country: string;
  description: string;
  impact: string;
}

// ── Foundries / Fabs ─────────────────────────────────────────────────────────

export const SEMI_FABS: SemiFab[] = [
  {
    id: 'tsmc-hsinchu',
    company: 'TSMC',
    country: 'Taiwan',
    countryCode: 'TW',
    city: 'Hsinchu',
    lat: 24.8, lon: 120.9,
    node: '3nm',
    capacity: '~60,000 wspm (advanced)',
    customers: ['Apple', 'NVIDIA', 'AMD', 'Qualcomm', 'Broadcom'],
    riskLevel: 'critical',
    riskReason: 'Taiwan Strait conflict threat. Produces ~90% of world\'s most advanced chips.',
    globalSharePct: 54,
  },
  {
    id: 'tsmc-tainan',
    company: 'TSMC',
    country: 'Taiwan',
    countryCode: 'TW',
    city: 'Tainan (Fab 18)',
    lat: 22.99, lon: 120.2,
    node: '5nm',
    capacity: '~100,000 wspm',
    customers: ['Apple', 'AMD', 'NVIDIA'],
    riskLevel: 'critical',
    riskReason: 'Same Taiwan Strait risk; 5nm node for A-series chips.',
    globalSharePct: 12,
  },
  {
    id: 'samsung-pyeongtaek',
    company: 'Samsung Foundry',
    country: 'South Korea',
    countryCode: 'KR',
    city: 'Pyeongtaek',
    lat: 37.0, lon: 127.1,
    node: '3nm',
    capacity: '~70,000 wspm',
    customers: ['Qualcomm', 'IBM', 'Tesla', 'Samsung LSI'],
    riskLevel: 'high',
    riskReason: 'North Korea missile/WMD threat. 2nd largest advanced foundry.',
    globalSharePct: 17,
  },
  {
    id: 'samsung-hwaseong',
    company: 'Samsung',
    country: 'South Korea',
    countryCode: 'KR',
    city: 'Hwaseong',
    lat: 37.2, lon: 127.0,
    node: '4nm',
    capacity: '~120,000 wspm',
    customers: ['Google', 'Samsung LSI', 'Vivo'],
    riskLevel: 'high',
    riskReason: 'Key DRAM + logic fab in proximity to Korean Peninsula tensions.',
    globalSharePct: 10,
  },
  {
    id: 'intel-oregon',
    company: 'Intel Foundry',
    country: 'United States',
    countryCode: 'US',
    city: 'Hillsboro, OR',
    lat: 45.5, lon: -122.9,
    node: '3nm',
    capacity: '~30,000 wspm (ramp)',
    customers: ['Intel', 'Microsoft', 'AWS'],
    riskLevel: 'medium',
    riskReason: 'Ramping Intel 18A process. Geopolitically stable.',
    globalSharePct: 3,
  },
  {
    id: 'smic-shanghai',
    company: 'SMIC',
    country: 'China',
    countryCode: 'CN',
    city: 'Shanghai',
    lat: 31.2, lon: 121.5,
    node: '7nm',
    capacity: '~80,000 wspm',
    customers: ['Huawei', 'Xiaomi', 'CXMT'],
    riskLevel: 'high',
    riskReason: 'US export controls limit advanced EUV access. Huawei dependency.',
    globalSharePct: 6,
  },
  {
    id: 'globalfoundries-malta',
    company: 'GlobalFoundries',
    country: 'United States',
    countryCode: 'US',
    city: 'Malta, NY',
    lat: 42.9, lon: -73.8,
    node: '14nm',
    capacity: '~60,000 wspm',
    customers: ['AMD', 'Qualcomm', 'US DoD'],
    riskLevel: 'low',
    riskReason: 'Critical for US DoD / defense chips. Politically stable.',
    globalSharePct: 5,
  },
  {
    id: 'umc-hsinchu',
    company: 'UMC',
    country: 'Taiwan',
    countryCode: 'TW',
    city: 'Hsinchu',
    lat: 24.8, lon: 120.9,
    node: '28nm',
    capacity: '~100,000 wspm',
    customers: ['MediaTek', 'Texas Instruments', 'NXP'],
    riskLevel: 'high',
    riskReason: 'Taiwan-based, mature node critical for automotive/IoT.',
    globalSharePct: 6,
  },
  {
    id: 'sk-hynix-icheon',
    company: 'SK Hynix',
    country: 'South Korea',
    countryCode: 'KR',
    city: 'Icheon',
    lat: 37.3, lon: 127.5,
    node: '10nm',
    capacity: '~200,000 wspm (DRAM)',
    customers: ['Apple', 'NVIDIA', 'Dell', 'HP'],
    riskLevel: 'high',
    riskReason: 'HBM3 monopoly for AI chips (NVIDIA H100/H200). KP threat.',
    globalSharePct: 8,
  },
];

// ── Equipment (Chokepoints) ───────────────────────────────────────────────────

export const SEMI_EQUIPMENT: SemiEquipment[] = [
  {
    company: 'ASML',
    country: 'Netherlands',
    countryCode: 'NL',
    product: 'EUV Lithography (TWINSCAN)',
    monopolyPct: 100,
    riskLevel: 'critical',
    note: 'Only company capable of producing EUV machines (~$370M each). Required for all sub-7nm chips.',
  },
  {
    company: 'ASML',
    country: 'Netherlands',
    countryCode: 'NL',
    product: 'DUV Lithography',
    monopolyPct: 85,
    riskLevel: 'high',
    note: 'DUV (Deep UV) covers 7nm-28nm. Export controls to China impacting SMIC.',
  },
  {
    company: 'Applied Materials (AMAT)',
    country: 'United States',
    countryCode: 'US',
    product: 'CVD / PVD Deposition',
    monopolyPct: 35,
    riskLevel: 'high',
    note: 'Largest semi equipment company. Critical for thin film deposition.',
  },
  {
    company: 'Lam Research',
    country: 'United States',
    countryCode: 'US',
    product: 'Etch Systems',
    monopolyPct: 42,
    riskLevel: 'high',
    note: 'Dominant in atomic layer etch (ALE) for 3D NAND & logic.',
  },
  {
    company: 'KLA Corporation',
    country: 'United States',
    countryCode: 'US',
    product: 'Inspection & Metrology',
    monopolyPct: 54,
    riskLevel: 'high',
    note: 'Quality control at every fab step. No advanced chip without KLA tools.',
  },
  {
    company: 'Tokyo Electron (TEL)',
    country: 'Japan',
    countryCode: 'JP',
    product: 'Coater / Developer / CVD',
    monopolyPct: 28,
    riskLevel: 'medium',
    note: 'Japan export controls align with US/Netherlands on China chip restrictions.',
  },
  {
    company: 'Synopsys / Cadence',
    country: 'United States',
    countryCode: 'US',
    product: 'EDA Software (Chip Design)',
    monopolyPct: 75,
    riskLevel: 'critical',
    note: 'Without EDA tools, chip design impossible. US export controls = China design embargo.',
  },
  {
    company: 'Arm Holdings',
    country: 'United Kingdom',
    countryCode: 'GB',
    product: 'CPU/GPU IP Cores',
    monopolyPct: 99,
    riskLevel: 'critical',
    note: 'Virtually all mobile chips use ARM architecture. US-UK strategic asset.',
  },
];

// ── Critical Materials ─────────────────────────────────────────────────────────

export const SEMI_MATERIALS: SemiMaterial[] = [
  {
    material: 'Neon Gas (lithography)',
    topSupplier: 'Ingas / Cryoin',
    supplierCountry: 'Ukraine',
    countryCode: 'UA',
    globalSharePct: 45,
    riskLevel: 'critical',
    note: 'Russia-Ukraine war disrupted supply in 2022. Used in EUV/DUV laser chambers.',
  },
  {
    material: 'Palladium (sensors)',
    topSupplier: 'Norilsk Nickel',
    supplierCountry: 'Russia',
    countryCode: 'RU',
    globalSharePct: 43,
    riskLevel: 'high',
    note: 'Sanctions on Russian metals threaten multilayer ceramic capacitor supply.',
  },
  {
    material: 'Gallium (GaAs / GaN chips)',
    topSupplier: 'State reserves',
    supplierCountry: 'China',
    countryCode: 'CN',
    globalSharePct: 80,
    riskLevel: 'critical',
    note: 'China export controls on Gallium (2023). Critical for RF/defense chips.',
  },
  {
    material: 'Germanium (fiber optics, IR)',
    topSupplier: 'State reserves',
    supplierCountry: 'China',
    countryCode: 'CN',
    globalSharePct: 60,
    riskLevel: 'critical',
    note: 'China Ge export ban (2023). Used in night vision, fiber, semiconductors.',
  },
  {
    material: 'Silicon Wafers (300mm)',
    topSupplier: 'Shin-Etsu / SUMCO',
    supplierCountry: 'Japan',
    countryCode: 'JP',
    globalSharePct: 56,
    riskLevel: 'medium',
    note: 'Japan dominates ultra-pure silicon wafer supply. Earthquake risk (Kumamoto).',
  },
  {
    material: 'Rare Earth Elements',
    topSupplier: 'CREC / State',
    supplierCountry: 'China',
    countryCode: 'CN',
    globalSharePct: 85,
    riskLevel: 'critical',
    note: 'Neodymium, Dysprosium for motors/sensors. China controls global REE supply.',
  },
  {
    material: 'Fluorine Compounds (etchants)',
    topSupplier: 'Stella Chemifa / Kanto',
    supplierCountry: 'Japan',
    countryCode: 'JP',
    globalSharePct: 70,
    riskLevel: 'high',
    note: 'High-purity HF (hydrofluoric acid) critical for silicon etching.',
  },
  {
    material: 'Photoresist (EUV)',
    topSupplier: 'JSR / Tokyo Ohka',
    supplierCountry: 'Japan',
    countryCode: 'JP',
    globalSharePct: 90,
    riskLevel: 'critical',
    note: 'JSR nationalized by Japan govt (2023). EUV photoresist = strategic asset.',
  },
];

// ── Strategic Chokepoints ──────────────────────────────────────────────────────

export const SEMI_CHOKES: SemiChoke[] = [
  {
    id: 'taiwan-strait',
    title: 'Taiwan Strait Conflict',
    type: 'fab',
    severity: 'critical',
    country: 'Taiwan',
    description: 'TSMC produces ~54% of global foundry revenue and 90%+ of sub-7nm chips. Any Taiwan Strait conflict would halt production.',
    impact: 'Global GDP loss estimated $1T+/year. AI, defense, automotive, smartphones all halt within months.',
  },
  {
    id: 'asml-export',
    title: 'ASML EUV Export Restrictions',
    type: 'equipment',
    severity: 'critical',
    country: 'Netherlands/China',
    description: 'Netherlands/US ban on ASML EUV (and now DUV) exports to China. China cannot produce sub-14nm chips at scale.',
    impact: 'Chinese foundries (SMIC) stuck at 7nm workarounds. Huawei Kirin 9000s limited to 5-year-old performance.',
  },
  {
    id: 'china-gallium-ge',
    title: 'China Gallium/Germanium Export Ban',
    type: 'material',
    severity: 'critical',
    country: 'China',
    description: 'China controls 80% of gallium and 60% of germanium. Export controls announced Aug 2023 as retaliation for US chip sanctions.',
    impact: 'RF chips (5G, defense radar), IR sensors, fiber optics at risk. 6-12 month stockpile only.',
  },
  {
    id: 'korea-missile-threat',
    title: 'Korean Peninsula DRAM Risk',
    type: 'fab',
    severity: 'high',
    country: 'South Korea',
    description: 'Samsung + SK Hynix produce ~70% of global DRAM. DPRK missile tests and potential conflict threaten Seoul-area fabs.',
    impact: 'DRAM shortage = all servers, PCs, smartphones affected. AI training infrastructure halted.',
  },
  {
    id: 'japan-photoresist',
    title: 'Japan Photoresist / Chemical Controls',
    type: 'material',
    severity: 'high',
    country: 'Japan',
    description: 'Japan controls 90% of EUV photoresist (JSR, TOK). Japan also exports restrictions on fluorine/HF to Korea (2019 precedent).',
    impact: 'TSMC/Samsung EUV production halted within weeks without Japanese chemicals.',
  },
  {
    id: 'arm-license-revoke',
    title: 'ARM License Disruption (Huawei precedent)',
    type: 'design',
    severity: 'high',
    country: 'United Kingdom/US',
    description: 'ARM revoked Huawei license in 2020 under US export controls. All mobile chip designers depend on ARM IP.',
    impact: 'Any sanctioned entity cannot design new ARM-based chips. China building RISC-V alternatives (3-5yr lag).',
  },
];

export function getSemiRiskScore(): number {
  const criticals = SEMI_CHOKES.filter((c) => c.severity === 'critical').length;
  const highs = SEMI_CHOKES.filter((c) => c.severity === 'high').length;
  return Math.min(100, criticals * 20 + highs * 10);
}
